import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  useTheme,
  IconButton,
  Tooltip,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import moment from "moment/moment";

const CustodianTable = ({
  custodians,
  onAdd,
  onEdit,
  onDelete,
  searchTerm,
  onSearchChange,
  page,
  onPageChange,
  pageSize,
  onPageSizeChange,
}) => {
  const theme = useTheme();
  const columns = [
    {
      field: "name",
      headerName: "Full Name",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "abbreviation",
      headerName: "Abbreviation",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "date",
      headerName: "Creation Date",
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => {
        return params.value ? moment(params.value).format("MMM DD, YYYY") : "-";
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => onEdit(params.row)}
              sx={{
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.primary.light + "20",
                },
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => {
                onDelete(params.row);
              }}
              sx={{
                color: theme.palette.error.main,
                "&:hover": {
                  backgroundColor: theme.palette.error.light + "20",
                },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const filteredData = useMemo(() => {
    if (!searchTerm) return custodians;
    
    return custodians.filter((custodian) =>
      custodian.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      custodian.abbreviation?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [custodians, searchTerm]);

  // Add id field to each row for DataGrid
  const rowsWithId = filteredData.map((custodian) => ({
    ...custodian,
    id: custodian.custodianId || Math.random().toString(),
  }));

  return (
    <Box sx={{  width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search custodians..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "text.disabled" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 300,
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
              },
            }}
          />
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAdd}
          sx={{
            borderRadius: 1,
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          Add Custodian
        </Button>
      </Box>

      <DataGridPro
        rows={rowsWithId}
        columns={columns}
        pageSize={1000}
        onPageSizeChange={(newPageSize) => onPageSizeChange(newPageSize)}
        rowsPerPageOptions={[25, 50, 100, 500, 1000, 2000]}
        pagination
        density="compact"
        disableSelectionOnClick
        sx={{
          border: "none",
          height: "calc(100vh - 20px)",
          "& .MuiDataGrid-cell": {
            borderBottom: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.primary,
            fontWeight: 500,
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.grey[100],
            borderBottom: `2px solid ${theme.palette.divider}`,
          },
          "& .MuiDataGrid-columnHeader": {
            fontWeight: 600,
            color: theme.palette.text.primary,
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: theme.palette.action.hover,
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: "transparent",
          },
        }}
      />
    </Box>
  );
};

export default CustodianTable; 